import { useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';

/**
 * Hook for Firebase Storage file uploads with progress tracking
 */
export const useStorage = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  /**
   * Upload a single file to Firebase Storage
   * @param {File} file - The file to upload
   * @param {string} path - Storage path (e.g. "bounties/bountyId/image.jpg")
   * @returns {Promise<string>} Download URL of the uploaded file
   */
  const uploadFile = (file, path) => {
    return new Promise((resolve, reject) => {
      setUploading(true);
      setError(null);
      setProgress(0);

      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setProgress(pct);
        },
        (err) => {
          setError(err.message);
          setUploading(false);
          reject(err);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setUploading(false);
          setProgress(100);
          resolve(downloadURL);
        }
      );
    });
  };

  /**
   * Upload multiple files
   * @param {File[]} files - Array of files
   * @param {string} basePath - Base storage path
   * @returns {Promise<string[]>} Array of download URLs
   */
  const uploadFiles = async (files, basePath) => {
    setUploading(true);
    const urls = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.split('.').pop();
      const path = `${basePath}/${Date.now()}_${i}.${ext}`;
      const url = await uploadFile(file, path);
      urls.push(url);
      setProgress(Math.round(((i + 1) / files.length) * 100));
    }

    setUploading(false);
    return urls;
  };

  /**
   * Delete a file from Firebase Storage by its URL
   * @param {string} url - Download URL of the file to delete
   */
  const deleteFile = async (url) => {
    try {
      const fileRef = ref(storage, url);
      await deleteObject(fileRef);
    } catch (err) {
      console.error('Error deleting file:', err);
    }
  };

  return { uploadFile, uploadFiles, deleteFile, uploading, progress, error };
};
